import { coreTypes } from "@core/core-types.di";
import {
    DismissAllNotificationsDto,
    NotificationDto,
    UnreadCountDto,
} from "@core/data/dto/notification.dto";
import type { Http } from "@core/data/infrastructures/http/http";
import { NotificationMapper } from "@core/data/mappers/notification.mapper";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { Notification } from "@core/domain/models/notification.model";
import { Either } from "@core/domain/types/either";
import { plainToClass } from "class-transformer";
import { inject, injectable } from "inversify";

@injectable()
export class NotificationDatasource {
    constructor(
        @inject(coreTypes.infrastructure.Http) private readonly http: Http,
        @inject(NotificationMapper)
        private readonly mapper: NotificationMapper,
    ) {}

    async fetchMy(limit = 20): Promise<Either<FallbackError, Notification[]>> {
        const result = await this.http.get<NotificationDto[]>(
            `/notifications/?limit=${limit}`,
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => {
                const data = response.data as unknown;

                // DRF may return either a plain array or a paginated object: { results: [...] }
                const items: unknown[] = Array.isArray(data)
                    ? data
                    : (data as { results?: unknown[] } | null)?.results ?? [];

                return items
                    .map((n) =>
                        this.mapper.map(
                            plainToClass(NotificationDto, n as object),
                        ),
                    )
                    .filter((n): n is Notification => n !== null);
            });
    }

    async fetchUnreadCount(): Promise<Either<FallbackError, number>> {
        const result = await this.http.get<UnreadCountDto>(
            "/notifications/unread_count/",
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => response.data.count ?? 0);
    }

    async markRead(id: number): Promise<Either<FallbackError, true>> {
        const result = await this.http.patch<
            NotificationDto,
            { read: boolean }
        >(`/notifications/${id}/mark_read/`, { read: true });

        return result
            .mapLeft(() => new FallbackError())
            .map(() => true as const);
    }

    async dismiss(id: number): Promise<Either<FallbackError, true>> {
        const result = await this.http.post<NotificationDto>(
            `/notifications/${id}/dismiss/`,
        );

        return result
            .mapLeft(() => new FallbackError())
            .map(() => true as const);
    }

    async dismissAll(): Promise<
        Either<FallbackError, DismissAllNotificationsDto>
    > {
        const result = await this.http.post<DismissAllNotificationsDto>(
            "/notifications/dismiss_all/",
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => response.data);
    }
}
